import Icon from '@ant-design/icons';
import { Popover } from 'antd';
import classNames from 'classnames';
import { useContext, useEffect, useState } from 'react';

import DownSvg from '@/components/Icon/svgs/down.svg';
import type { SessionSourceInterface } from '@/components/chat-app/utils/utils';
import { CONTAINER_EVENT } from '@/components/chat-app/utils/utils';
import { ContextGeniusLayout } from '@/context/GeniusLayout';
import useGenius from '@/hooks/useGenius';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { getSessionId } from '@/service/create';
import { getTaskName } from '@/service/task';
import { ENoticeType } from '@/types/wsNotice';
import type { ISourceCustomForChatTitleUpdate, TNotice } from '@/types/wsNotice';
import Task from '@/ui/components/task';
import { EVENT } from '@/utils/event';
import reportClick, { EReportClickKey } from '@/utils/report/reportClick';

import styles from './index.less';

interface IUpdateTaskName {
  sessionId: string;
  title: string;
}

const ChatTitle = (props: { layoutSize: LAYOUT_SIZE }) => {
  const { t } = useTranslation();

  const defaultTaskName = t('chatApp.header.history');
  const { layoutSize } = props;

  const { shadowMindId: mindId } = useGenius();
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [title, setTitle] = useState<string>(defaultTaskName);
  const [topBarTaskOpen, setTopBarTaskOpen] = useStorage<boolean>('topBarTaskOpen');

  const { chatStatus } = useContext(ContextGeniusLayout);

  useEffect(() => {
    const handleCloseTaskList = () => {
      setTopBarTaskOpen(false);
    };

    addEventListener(EVENT.CLOSE_TASK_LIST, handleCloseTaskList);

    return () => {
      removeEventListener(EVENT.CLOSE_TASK_LIST, handleCloseTaskList);
    };
  }, [setTopBarTaskOpen]);

  useEffect(() => {
    function newChat() {
      setTitle(defaultTaskName);
    }
    addEventListener(EVENT.NEW_CHAT_CALLBACK, newChat);

    return () => {
      removeEventListener(EVENT.NEW_CHAT_CALLBACK, newChat);
    };
  }, [defaultTaskName]);

  useEffect(() => {
    const handleContinue = (e: Event) => {
      const { detail } = e as CustomEvent;

      tryGetTaskName(detail);

      dispatchEvent(new CustomEvent(EVENT.SET_CURRENT_SESSION_ID, { detail: detail }));
      dispatchEvent(new Event(EVENT.CLOSE_TASK_LIST));
      dispatchEvent(
        new CustomEvent<SessionSourceInterface>(CONTAINER_EVENT.OPEN_SESSION_WITH_SOURCE, {
          detail: {
            sessionId: detail
          }
        })
      );
    };

    addEventListener(EVENT.CONTINUE_CHAT, handleContinue);

    return () => {
      removeEventListener(EVENT.CONTINUE_CHAT, handleContinue);
    };
  }, []);

  useEffect(() => {
    const handleSetSessionId = (e: Event) => {
      setCurrentSessionId((e as CustomEvent).detail);
    };

    addEventListener(EVENT.SET_CURRENT_SESSION_ID, handleSetSessionId);

    return () => {
      removeEventListener(EVENT.SET_CURRENT_SESSION_ID, handleSetSessionId);
    };
  }, []);

  useEffect(() => {
    const wsNotice = (event: Event) => {
      const e = event as CustomEvent;
      const data: TNotice = e.detail as TNotice;
      const sourceCustom = data.sourceCustom;

      if (data.sourceType === ENoticeType.chat) {
        const _sourceData = sourceCustom as ISourceCustomForChatTitleUpdate;

        if (_sourceData.type === 'titleUpdate' && _sourceData.sessionId == currentSessionId) {
          setTitle(_sourceData.title);
        }
      }
    };

    addEventListener(EVENT.WS_NOTICE, wsNotice);

    return () => {
      removeEventListener(EVENT.WS_NOTICE, wsNotice);
    };
  }, [currentSessionId]);

  useEffect(() => {
    const handleTaskName = (e: Event) => {
      const { detail } = e as CustomEvent;
      const taskData = detail as IUpdateTaskName;

      if (taskData.sessionId == currentSessionId) setTitle(taskData.title);
    };

    addEventListener(EVENT.SET_CURRENT_TASK_NAME, handleTaskName);

    return () => {
      removeEventListener(EVENT.SET_CURRENT_TASK_NAME, handleTaskName);
    };
  }, [currentSessionId]);

  const tryGetTaskName = (originSessionId?: string) => {
    let attempts = 0;

    const handleGetTaskName = async () => {
      const sessionId = originSessionId || (await getSessionId());

      if (!sessionId) return;

      getTaskName(sessionId).then((res) => {
        if (res.data.code === 0) {
          const newTaskName = res.data.data;

          if (newTaskName && newTaskName !== title) {
            setTitle(newTaskName);

            return;
          }

          // 超过四次后，如果还是没有获取到taskName，就使用默认的taskName
          if (attempts >= 4 && !newTaskName) {
            setTitle(defaultTaskName);
          }
        }

        if (attempts < 4) {
          attempts += 1;
          setTimeout(() => handleGetTaskName(), 1500);
        }
      });
    };

    handleGetTaskName();
  };

  useEffect(() => {
    if (chatStatus === 'hide' && topBarTaskOpen) {
      setTopBarTaskOpen(false);
    }
  }, [chatStatus, setTopBarTaskOpen, topBarTaskOpen, layoutSize]);

  if (!mindId) return null;

  return (
    <Popover
      arrow={false}
      content={<Task mindId={mindId} sessionId={currentSessionId} />}
      destroyTooltipOnHide
      open={topBarTaskOpen && layoutSize !== LAYOUT_SIZE.LARGE}
      overlayClassName={classNames(styles.overlayClassName)}
      placement={'bottom'}
    >
      <div
        className={classNames(styles.taskContainer, {
          [styles.topBarTaskOpen]: topBarTaskOpen
        })}
        onClick={() => {
          setTopBarTaskOpen((o) => {
            if (!o) {
              reportClick(EReportClickKey.clicked_layout_top_bar, { key1: 'task' });
            }

            return !o;
          });
        }}
      >
        <span className={styles.title}>{title}</span>
        <Icon className={styles.dropdownIcon} component={DownSvg} />
      </div>
    </Popover>
  );
};

export default ChatTitle;
